import { Button, Paper, PaperProps, Stack, Typography } from '@mui/material';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DisplayCurrency } from '@/components/DisplayCurrency';
import { useTransactionContext } from '@/context/transaction';
import { boldWeight } from '@/theme';
import { isFiniteNumber } from '@/utils';

export type TransactionIntentFooterProps = {
    onCancel?: () => void;
    onConfirm: () => void;
    confirmLabel?: string;
    /**
     * If not specified, confirming requires `hasChanges && hasPayment` by default.
     */
    canConfirm?: boolean;
} & PaperProps;

export const TransactionIntentFooter = ({
    onCancel,
    onConfirm,
    children,
    confirmLabel,
    canConfirm,
    sx,
    ...props
}: TransactionIntentFooterProps) => {
    const context = useTransactionContext();
    const {
        isPending: isTransactionLoading,
        isUpdating: isTransactionUpdating,
        type,
        backTarget,
        hasChanges,
        hasPayment,
        totalDueLabel,
        track,
    } = context;
    const { totalDue = undefined, currency = undefined } = isTransactionLoading ? {} : context;
    const isConfirmationPending = false;
    const navigate = useNavigate();
    const [lastSubmitButtonLabel, setLastSubmitButtonLabel] = useState(confirmLabel ?? 'Confirm');

    const enableConfirm = useMemo(() => {
        if (isConfirmationPending || isTransactionLoading || isTransactionUpdating) {
            return false;
        }
        if (!isNil(canConfirm)) {
            return canConfirm;
        }
        return hasChanges && hasPayment;
    }, [canConfirm, hasChanges, hasPayment, isConfirmationPending, isTransactionLoading, isTransactionUpdating]);

    const submitButtonLabel = useMemo(() => {
        if (confirmLabel) {
            return confirmLabel;
        }
        if (isTransactionLoading) {
            // it will be disabled but give it some text so people can see where
            // confirmation is going to end up
            return lastSubmitButtonLabel;
        }
        if (type === 'subscription' && context.prorationBehavior === 'none') {
            // no immediate charge or refund; just save to take effect next invoice
            // (as of 2025, just applies to edit recurring sat credits)
            return 'Save';
        }
        if (!isFiniteNumber(totalDue)) {
            // if we're loading a new total, defer to current label
            return lastSubmitButtonLabel;
        }

        return isFiniteNumber(totalDue) && totalDue > 0 ? 'Purchase' : 'Confirm';
    }, [confirmLabel, isTransactionLoading, type, context, totalDue, lastSubmitButtonLabel]);

    useEffect(() => {
        setLastSubmitButtonLabel(submitButtonLabel);
    }, [submitButtonLabel]);

    function handleCancel() {
        track('Transaction cancelled', { backTarget });

        if (onCancel) {
            onCancel();
            return;
        }

        if (backTarget) {
            navigate(backTarget.href);
        } else {
            navigate('/');
        }
    }

    function handleConfirm() {
        track('Transaction confirmed', { button: submitButtonLabel });
        onConfirm();
    }

    return (
        <Paper
            elevation={4}
            sx={{
                position: 'fixed',
                zIndex: 1,
                left: 0,
                right: 0,
                bottom: 0,
                p: 3,
                mt: 1,
                bgcolor: 'white',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'end',
                gap: 1,
                ...sx,
            }}
            {...props}
        >
            {children}
            <Stack direction="row" sx={{ justifySelf: 'end' }}>
                <Stack
                    direction="row"
                    data-testid="total-due-readout"
                    sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1, ...sx }}
                >
                    <Typography sx={{ fontWeight: boldWeight }}>{totalDueLabel}:</Typography>
                    <DisplayCurrency
                        variant={'total'}
                        value={isTransactionUpdating ? undefined : totalDue}
                        stickyValueOnLoading
                        cents
                        currency={currency}
                    />
                </Stack>
                <Button onClick={handleCancel} disabled={isConfirmationPending}>
                    Cancel
                </Button>
                <Button
                    data-testid="transaction-confirm-button"
                    variant="contained"
                    disabled={!enableConfirm}
                    autoFocus
                    onClick={handleConfirm}
                >
                    {submitButtonLabel}
                </Button>
            </Stack>
        </Paper>
    );
};
